import { useMemo, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Badge, Button, Input, Label, Textarea } from '@artstorefronts/ui';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import AdminLayout from '../../../components/AdminLayout';
import { FieldGroup, GROUPS, type Field } from './fields';

const ADMIN_FEED_STRATEGIES = gql`
  query AdminFeedStrategies {
    adminFeedStrategies {
      strategies {
        id
        name
        description
        configOverrides
        isDefault
        isArchived
      }
      fields {
        key
        group
        label
        description
        default
        min
        max
        isFloat
        isToggle
      }
    }
  }
`;

const CREATE = gql`
  mutation AdminCreateFeedStrategy($input: FeedStrategyInput!) {
    adminCreateFeedStrategy(input: $input) {
      id
    }
  }
`;
const UPDATE = gql`
  mutation AdminUpdateFeedStrategy($id: ID!, $input: FeedStrategyInput!) {
    adminUpdateFeedStrategy(id: $id, input: $input) {
      id
    }
  }
`;
const PROMOTE = gql`
  mutation AdminPromoteFeedStrategy($id: ID!) {
    adminPromoteFeedStrategy(id: $id) {
      id
    }
  }
`;
const ARCHIVE = gql`
  mutation AdminArchiveFeedStrategy($id: ID!) {
    adminArchiveFeedStrategy(id: $id) {
      id
    }
  }
`;

type Strategy = {
  id: string;
  name: string;
  description: string | null;
  configOverrides: Record<string, number>;
  isDefault: boolean;
  isArchived: boolean;
};
type Draft = { name: string; description: string; values: Record<string, string> };

function draftFor(fields: Field[], strategy: Strategy | null): Draft {
  const values = fields.reduce<Record<string, string>>((memo, field) => {
    const stored = strategy?.configOverrides?.[field.key];
    memo[field.key] = String(stored ?? field.default);
    return memo;
  }, {});
  return { name: strategy?.name ?? '', description: strategy?.description ?? '', values };
}

function AdminFeedStrategies() {
  const { data, loading, refetch } = useQuery(ADMIN_FEED_STRATEGIES, { fetchPolicy: 'cache-and-network' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const [create] = useMutation(CREATE);
  const [update] = useMutation(UPDATE);
  const [promote] = useMutation(PROMOTE);
  const [archive] = useMutation(ARCHIVE);

  const fields: Field[] = data?.adminFeedStrategies?.fields ?? [];
  const strategies: Strategy[] = data?.adminFeedStrategies?.strategies ?? [];
  const selected = useMemo(() => strategies.find((s) => s.id === selectedId) ?? null, [strategies, selectedId]);
  const activeDraft = draft ?? draftFor(fields, selected);
  const idleLabel = selectedId ? 'Save changes' : 'Create strategy';
  const savingLabel = selectedId ? 'Saving…' : 'Creating…';
  const saveLabel = saving ? savingLabel : idleLabel;

  function open(strategy: Strategy | null) {
    setSelectedId(strategy?.id ?? null);
    setDraft(draftFor(fields, strategy));
  }

  function setValue(key: string, value: string) {
    setDraft({ ...activeDraft, values: { ...activeDraft.values, [key]: value } });
  }

  async function save() {
    if (savingRef.current) return;
    // Number('') is 0, which the server would clamp to the knob's minimum — refuse instead.
    const blank = fields.find((field) => String(activeDraft.values[field.key] ?? '').trim() === '');
    if (blank) {
      toast.error(`${blank.label} needs a value`);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    const configOverrides = fields.reduce<Record<string, number>>((memo, field) => {
      memo[field.key] = Number(activeDraft.values[field.key]);
      return memo;
    }, {});
    const input = { name: activeDraft.name, description: activeDraft.description, configOverrides };
    try {
      if (selectedId) {
        await update({ variables: { id: selectedId, input } });
        toast.success('Strategy saved');
      } else {
        await create({ variables: { input } });
        toast.success('Strategy created');
      }
      setDraft(null);
      setSelectedId(null);
      await refetch();
    } catch (err: any) {
      toast.error(err?.message ?? 'Save failed');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  async function run(action: () => Promise<unknown>, ok: string) {
    try {
      await action();
      toast.success(ok);
      await refetch();
    } catch (err: any) {
      toast.error(err?.message ?? 'Action failed');
    }
  }

  if (loading && !data) {
    return (
      <AdminLayout title="Feed Strategies" description="Loading…">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Feed Strategies"
      description="Tune the community feeds and promote a strategy live — no deploy. The live strategy's knobs drive ranking, promoted-post spacing, new-member spacing, personalization and feed-card spacing."
      action={<Button onClick={() => open(null)}>New strategy</Button>}
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
        <div className="space-y-2">
          {strategies.map((strategy) => (
            <button
              key={strategy.id}
              onClick={() => open(strategy)}
              className={`flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left ${
                strategy.id === selectedId ? 'border-gray-900 bg-gray-50' : 'border-gray-200'
              }`}
            >
              <span className="font-medium text-gray-900">{strategy.name}</span>
              <span className="flex gap-1">
                {strategy.isDefault && <Badge>Live</Badge>}
                {strategy.isArchived && <Badge variant="secondary">Archived</Badge>}
              </span>
            </button>
          ))}
          {strategies.length === 0 && <p className="text-sm text-gray-500">No strategies yet.</p>}
        </div>

        <div className="space-y-5 rounded-lg border border-gray-200 p-6">
          <div className="space-y-2">
            <Label htmlFor="fs-name">Name</Label>
            <Input
              id="fs-name"
              value={activeDraft.name}
              onChange={(e) => setDraft({ ...activeDraft, name: e.target.value })}
              placeholder="e.g. Spread new members further"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fs-desc">Description</Label>
            <Textarea
              id="fs-desc"
              value={activeDraft.description}
              onChange={(e) => setDraft({ ...activeDraft, description: e.target.value })}
            />
          </div>

          {GROUPS.map((group) => (
            <FieldGroup
              key={group.key}
              group={group}
              fields={fields.filter((field) => field.group === group.key)}
              values={activeDraft.values}
              onChange={setValue}
            />
          ))}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saveLabel}
            </Button>
            {selected && !selected.isDefault && (
              <Button
                variant="outline"
                onClick={() => run(() => promote({ variables: { id: selected.id } }), 'Promoted live')}
              >
                Promote to live
              </Button>
            )}
            {selected && !selected.isDefault && !selected.isArchived && (
              <Button
                variant="outline"
                onClick={() => run(() => archive({ variables: { id: selected.id } }), 'Archived')}
              >
                Archive
              </Button>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminFeedStrategies;
